import { useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Button as ZusButton, Chip as ZusChip } from '@zep-us/zus';
import { ChevronRightIcon } from '@zep-us/zus-icons';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import { logger } from '@zep/core-logger';
import { useOpenWindow } from '@/hooks';
import { ExportIcon, XIcon } from '@/icons';
import { useUser } from '@/lib/auth';
import { usePaymentFlow } from '@/module/payment';
import { getPaymentLoginUrl } from '@/module/payment/shared/utils/paymentEntryUrl';
import { getInquiryLinkByCountryCode } from '@/module/plan/constants/inquiryLinks';
import { useCountryCode } from '@/shared/hooks';
import { type PlanType as _PlanType } from '@/types';
import { Button, Drawer, DrawerContent, IconButton, ResponsiveDialog } from '@/ui_v3';
import { cn } from '@/utils/tailwind.util';
import { BasicPricingCard, ProPricingCard } from '../PricingCard';
import { Features } from './Features';
import { SelectableCard } from './SelectableCard';
import { Titles } from './Titles';
type PlanType = Exclude<_PlanType, 'FREE'>;
type UpsellingModalCtaTarget = 'payment-flow' | 'pricing';
const UPSELLING_MOBILE_MAX_WIDTH = 810;
export const upsellingModal = {
  open({
    initialPlanType,
    from,
    ctaTarget = 'payment-flow',
    onClose
  }: {
    initialPlanType: PlanType;
    from: string;
    ctaTarget?: UpsellingModalCtaTarget;
    onClose?: VoidFunction;
  }) {
    overlay.open(({
      unmount
    }) => <UpsellingModal initialSelectedPlan={initialPlanType} from={from} ctaTarget={ctaTarget} onClose={() => {
      unmount();
      onClose?.();
    }} />);

    // 레거시 로깅
    logger.track('proplan_show', {
      location: from,
      planType: initialPlanType
    });
    logger.track('open_upselling_modal', {
      from,
      planType: initialPlanType
    });
  }
};
const UpsellingModal = ({
  initialSelectedPlan,
  from,
  ctaTarget,
  onClose
}: {
  initialSelectedPlan: PlanType;
  from: string;
  ctaTarget: UpsellingModalCtaTarget;
  onClose?: VoidFunction;
}) => {
  const isMobile = useMediaQuery({
    query: `(max-width: ${UPSELLING_MOBILE_MAX_WIDTH}px)`
  });
  return <>
      {isMobile ? <MobileView initialSelectedPlan={initialSelectedPlan} from={from} ctaTarget={ctaTarget} onClose={onClose} /> : <DesktopView from={from} ctaTarget={ctaTarget} onClose={onClose} />}
    </>;
};

// KR 전용(향상된 QUIZ AI+ 노출과 동일한 countryCode === 'KR' 기준).
// 상단에 학교·단체 견적 문의(/pricing/quote) 진입 알약.
const SchoolGroupInquiryPill = ({
  className
}: {
  className?: string;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const {
    isKR
  } = useCountryCode();
  const openWindow = useOpenWindow();
  if (!isKR) {
    return null;
  }
  return <div className={cn('flex', className)} data-sentry-component="SchoolGroupInquiryPill" data-sentry-source-file="UpsellingModal.tsx">
      <ZusButton variant="contained" intent="alternative" size="lg" rounded="full" onClick={() => {
      logger.track('click_get_quote_button');
      // 플랜 안내 페이지의 학교·단체 탭 활성 상태로 이동
      openWindow('/pricing?tab=group');
    }} rightSlot={<span className="flex items-center gap-3xs">
            <ZusChip size="md" rounded="default" className="min-w-[26px] border-pink-400/10 bg-pink-50 text-body-sm-semibold text-pink-400">
              {t('common:pricing.schoolGroup.discount40')}
            </ZusChip>
            <ChevronRightIcon size="sm" />
          </span>} data-sentry-element="ZusButton" data-sentry-source-file="UpsellingModal.tsx">
        {t('common:pricing.schoolGroup.question')}
      </ZusButton>
    </div>;
};
const DesktopView = ({
  from,
  ctaTarget,
  onClose
}: {
  from: string;
  ctaTarget: UpsellingModalCtaTarget;
  onClose?: VoidFunction;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const {
    startPaymentFlow
  } = usePaymentFlow();
  const openWindow = useOpenWindow();
  const {
    user,
    isAuthenticated
  } = useUser();
  const {
    countryCode
  } = useCountryCode();
  const userPlanType = user?.planType;
  const paymentCountryCode = user?.countryCode ?? countryCode;
  const isBasicUser = userPlanType === 'BASIC';
  const isProUser = userPlanType === 'PRO';
  const handleClickBasicCta = () => {
    logger.track('click_pricing_card_cta', {
      planType: 'BASIC',
      from
    });
    if (ctaTarget === 'pricing') {
      openWindow('/pricing');
      return;
    }
    if (!isAuthenticated) {
      openWindow(getPaymentLoginUrl('BASIC', paymentCountryCode));
      return;
    }
    startPaymentFlow({
      plan: 'BASIC',
      countryCode: paymentCountryCode
    }, {
      target: 'new-tab'
    });
  };
  const handleClickProCta = () => {
    logger.track('click_pricing_card_cta', {
      planType: 'PRO',
      from
    });
    if (ctaTarget === 'pricing') {
      openWindow('/pricing');
      return;
    }
    if (!isAuthenticated) {
      openWindow(getPaymentLoginUrl('PRO', paymentCountryCode));
      return;
    }
    if (isBasicUser) {
      window.open(getInquiryLinkByCountryCode(paymentCountryCode), '_blank', 'noopener,noreferrer');
      return;
    }
    startPaymentFlow({
      plan: 'PRO',
      countryCode: paymentCountryCode
    }, {
      target: 'new-tab'
    });
  };
  return <ResponsiveDialog size="2xl" className="flex-row" onClose={onClose} data-sentry-element="ResponsiveDialog" data-sentry-component="DesktopView" data-sentry-source-file="UpsellingModal.tsx">
      <SideSection data-sentry-element="SideSection" data-sentry-source-file="UpsellingModal.tsx" />
      <main className="relative flex w-full min-w-0 flex-col px-xl">
        <header className="flex shrink-0 flex-wrap items-center gap-md pb-md pt-lg">
          <SchoolGroupInquiryPill data-sentry-element="SchoolGroupInquiryPill" data-sentry-source-file="UpsellingModal.tsx" />
          <div className="ml-auto flex items-center gap-sm">
            <Button variant="transparent" intent="neutral" size="lg" rightSlot={<ExportIcon size="md" />} onClick={() => openWindow('/pricing')} data-sentry-element="Button" data-sentry-source-file="UpsellingModal.tsx">
              <span className="[text-box-edge:cap_alphabetic] [text-box-trim:trim-both]">
                {t('common:pricing.planGuide')}
              </span>
            </Button>
            <IconButton variant="transparent" intent="neutral" size="lg" aria-label={t('common:global.close.button')} onClick={() => onClose?.()} data-sentry-element="IconButton" data-sentry-source-file="UpsellingModal.tsx">
              <XIcon size="md" data-sentry-element="XIcon" data-sentry-source-file="UpsellingModal.tsx" />
            </IconButton>
          </div>
        </header>
        <div className="no-scrollbar min-h-0 flex-1 overflow-y-auto">
          <section className="flex w-full flex-col justify-center gap-md px-3xs pb-xl tablet-lg:flex-row">
            <BasicPricingCard className="min-w-0 hover:shadow-lg" ctaButtonText={isBasicUser ? t('common:upselling.currentlyUsing') : t('common:pricing.pro.purchase')} ctaButtonDisabled={isBasicUser} onCtaButtonClick={handleClickBasicCta} data-sentry-element="BasicPricingCard" data-sentry-source-file="UpsellingModal.tsx" />
            <ProPricingCard className="min-w-0 hover:shadow-lg" ctaButtonText={(() => {
            if (isProUser) {
              return t('common:upselling.currentlyUsing');
            }
            if (isBasicUser) {
              return t('common:pricing.pro.inquireNow');
            }
            return t('common:pricing.pro.purchase');
          })()} ctaButtonDisabled={isProUser} onCtaButtonClick={handleClickProCta} data-sentry-element="ProPricingCard" data-sentry-source-file="UpsellingModal.tsx" />
          </section>
        </div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] h-xl bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,rgba(255,255,255,1)_30%)]" />
      </main>
    </ResponsiveDialog>;
};
const MobileView = ({
  initialSelectedPlan,
  from,
  ctaTarget,
  onClose
}: {
  initialSelectedPlan: PlanType;
  from: string;
  ctaTarget: UpsellingModalCtaTarget;
  onClose?: VoidFunction;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const {
    countryCode
  } = useCountryCode();
  const {
    startPaymentFlow
  } = usePaymentFlow();
  const openWindow = useOpenWindow();
  const [selectedPlan, setSelectedPlan] = useState<PlanType>(initialSelectedPlan);
  const {
    user,
    isAuthenticated
  } = useUser();
  const paymentCountryCode = user?.countryCode ?? countryCode;
  const isUserUsingSamePlan = user?.planType === selectedPlan;
  const isBasicUser = user?.planType === 'BASIC';
  const handleClickCta = () => {
    logger.track('click_upselling_modal_mobile_cta', {
      from,
      planType: selectedPlan
    });
    if (ctaTarget === 'pricing') {
      openWindow('/pricing');
      return;
    }
    if (!isAuthenticated) {
      openWindow(getPaymentLoginUrl(selectedPlan, paymentCountryCode));
      return;
    }
    if (isBasicUser) {
      window.open(getInquiryLinkByCountryCode(paymentCountryCode), '_blank', 'noopener,noreferrer');
      return;
    }
    startPaymentFlow({
      plan: selectedPlan,
      countryCode: paymentCountryCode
    }, {
      target: 'new-tab'
    });
  };
  return <Drawer open={true} data-sentry-element="Drawer" data-sentry-component="MobileView" data-sentry-source-file="UpsellingModal.tsx">
      <DrawerContent className="!max-h-[calc(90%-env(safe-area-inset-top))] w-full" hasDrawerHandle={false} hasDim onDimClick={() => onClose?.()} data-sentry-element="DrawerContent" data-sentry-source-file="UpsellingModal.tsx">
        <div className="flex flex-col gap-lg overflow-y-auto bg-[linear-gradient(180deg,#FFF_0%,#E7F4FF_37.11%,#F3F2FF_74.93%)] px-md pb-[168px] pt-md">
          <Titles data-sentry-element="Titles" data-sentry-source-file="UpsellingModal.tsx" />
          <SchoolGroupInquiryPill className="justify-center pt-xs" data-sentry-element="SchoolGroupInquiryPill" data-sentry-source-file="UpsellingModal.tsx" />

          <section className="flex flex-col gap-md px-xs">
            <div className="flex flex-col gap-xs">
              <SelectableCard planType="BASIC" isSelected={selectedPlan === 'BASIC'} onSelect={setSelectedPlan} data-sentry-element="SelectableCard" data-sentry-source-file="UpsellingModal.tsx" />
              <SelectableCard planType="PRO" isSelected={selectedPlan === 'PRO'} onSelect={setSelectedPlan} data-sentry-element="SelectableCard" data-sentry-source-file="UpsellingModal.tsx" />
            </div>
            <Features planType={selectedPlan} countryCode={countryCode} data-sentry-element="Features" data-sentry-source-file="UpsellingModal.tsx" />
          </section>

          <footer className="absolute bottom-0 left-0 z-[2] flex w-full flex-col gap-xs bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,white_10%,white_100%)] p-md">
            <Button size="lg" intent="strong" disabled={isUserUsingSamePlan} onClick={handleClickCta} data-sentry-element="Button" data-sentry-source-file="UpsellingModal.tsx">
              {(() => {
              if (isUserUsingSamePlan) {
                return t('common:upselling.currentlyUsing');
              }
              if (isBasicUser) {
                return t('common:pricing.pro.inquireNow');
              }
              return t('common:pricing.purchaseSelectedPlan');
            })()}
            </Button>
            <Button size="md" variant="transparent" intent="neutral" rightSlot={<ExportIcon size="md" />} onClick={() => openWindow('/pricing')} data-sentry-element="Button" data-sentry-source-file="UpsellingModal.tsx">
              <span>{t('common:pricing.planGuide')}</span>
            </Button>
          </footer>
        </div>
      </DrawerContent>
    </Drawer>;
};
const SideSection = () => {
  return <aside className="relative w-[240px] shrink-0 bg-[url('/assets/upselling_background.webp')] bg-cover tablet-lg:w-[300px]" data-sentry-component="SideSection" data-sentry-source-file="UpsellingModal.tsx">
      <div className="flex flex-col items-center gap-lg px-lg py-3xl">
        <Titles data-sentry-element="Titles" data-sentry-source-file="UpsellingModal.tsx" />
      </div>
      <div className="absolute bottom-0 left-0 z-[1] h-[180px] w-full bg-[linear-gradient(180deg,rgba(231,232,255,0.00)_0%,#E7E4FF_100%)]" />
    </aside>;
};